import {
    SpaceBetween,
    BreadcrumbGroup,
    Header,
    Box,
    Table,
    Button,
    TextFilter,
    Pagination,
    CollectionPreferences,
    Link,
} from '@amzn/awsui-components-console';
import React, { useEffect, useState } from 'react';
import * as Constants from './constants';
import { listAllLabs, offboardLab } from './apis';
import { EmptyState, getFilterCounterText } from '../group/utils';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { useHistory } from 'react-router-dom';
import { isAdmin, getCurrentAuthUserEmail } from '../../lib/utils';

export default function LabManager() {
    /**
     * Bot account component
     */
    const history = useHistory();
    const [selectedLab, setSelectedLab] = React.useState([]);
    const [tableLoading, setTableLoading] = React.useState(false);
    const [currentUser, setCurrentUser] = React.useState({ email: '', admin: false });
    const [labs, setLabs] = React.useState([]);
    const [deleteDisabled, setDeleteDisabled] = React.useState(true);
    const [configureDisabled, setConfigureDisabled] = React.useState(true);
    const [preferences, setPreferences] = useState({
        pageSize: 20,
        stickyColumns: { first: 1 },
        contentDisplay: Constants.LAB_VISIBLE_COLUMNS,
    });
    const { items, actions, collectionProps, filteredItemsCount, filterProps, paginationProps } = useCollection(labs, {
        filtering: {
            noMatch: (
                <EmptyState
                    title="No matches"
                    subtitle="We can’t find a match."
                    action={<Button onClick={() => actions.setFiltering('')}>Clear filter</Button>}
                />
            ),
        },
        pagination: { pageSize: preferences.pageSize },
        sorting: {
            defaultState: { sortingColumn: Constants.SORTED_FILED, isDescending: true },
        },
        selection: {},
    });

    /**
     * process selection change event
     */
    const onSelectionChange = (selectedItems) => {
        // when lab is selected
        setSelectedLab(selectedItems);
        if (currentUser.admin || currentUser.email === selectedItems[0].owner) {
            setDeleteDisabled(false);
            setConfigureDisabled(false);
        } else {
            setDeleteDisabled(true);
            setConfigureDisabled(true);
        }
    };
    const startLoading = () => {
        // initialize loading status
        setTableLoading(true);
        setDeleteDisabled(true);
        setConfigureDisabled(true);
    };
    const endLoading = () => {
        // end loading status
        setTableLoading(false);
    };

    /**
     * load labs table
     */
    const loadTable = () => {
        (async function () {
            startLoading();
            try {
                const response = await listAllLabs();
                const labs = response['response'];
                setLabs(labs);
            } catch (e) {
                console.error('Fail to load lab(s) with error', e);
            }
            endLoading();
        })();
    };

    /**
     * process configure button click event
     */
    const onConfigureClick = () => {
        if (selectedLab.length === 0) {
            console.error('when no lab id is selected, configure button should not be enabled.');
        } else {
            const lab = selectedLab[0];
            history.push({
                pathname: `/lab-management/labs/configure/${lab['LabID']}`,
                state: lab,
            });
        }
    };

    /**
     * process configure button click event
     */
    const onOffboardClick = () => {
        if (selectedLab.length === 0) {
            console.error('when no lab id is selected, configure button should not be enabled.');
        } else {
            const lab = selectedLab[0];
            history.push({
                pathname: `/lab-management/labs/offboard/${lab['LabID']}`,
                state: lab,
            });
        }
    };

    useEffect(() => {
        // initialize the component
        loadTable();

        async function loadUser() {
            const currentAuthUserIsAdmin = await isAdmin();
            const user = await getCurrentAuthUserEmail();
            setCurrentUser({
                email: user,
                admin: currentAuthUserIsAdmin['status'],
            });
        }
        loadUser();
    }, []);

    return (
        <SpaceBetween size="s">
            <BreadcrumbGroup
                items={[
                    { text: 'DS2 Device Farm', href: '#' },
                    { text: 'Lab management', href: '#/lab-management/labs' },
                ]}
                ariaLabel="Breadcrumbs"
            />
            <Header
                variant="h1"
                description={
                    <>
                        This page shows all the labs you are allowed to view. You can configure and offboard only the
                        labs you own. For any questions you have, please consult our{' '}
                        <Link
                            external
                            href="https://w.amazon.com/bin/view/DS2DeviceFarm/Technical_Details/LabManagement"
                        >
                            wiki
                        </Link>
                        .
                    </>
                }
            >
                Lab management
            </Header>
            <Table
                {...collectionProps}
                onSelectionChange={({ detail }) => onSelectionChange(detail.selectedItems)}
                selectedItems={selectedLab}
                selectionType="single"
                header={
                    <Header
                        counter={`(${labs.length})`}
                        actions={
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button onClick={loadTable} iconName="refresh" variant="icon" />
                                <Button disabled={deleteDisabled} onClick={onOffboardClick}>
                                    Offboard
                                </Button>
                                <Button disabled={configureDisabled} onClick={onConfigureClick}>
                                    Configure
                                </Button>
                                <Button href="#/lab-management/labs/onboard" variant="primary">
                                    Onboard
                                </Button>
                            </SpaceBetween>
                        }
                    >
                        DDF labs
                    </Header>
                }
                columnDisplay={preferences.contentDisplay}
                columnDefinitions={Constants.LAB_COLUMNS_DEFINITION}
                items={items}
                pagination={<Pagination {...paginationProps} />}
                filter={<TextFilter {...filterProps} countText={getFilterCounterText(filteredItemsCount)} />}
                empty={
                    <Box textAlign="center" color="inherit">
                        <b>No labs to display</b>
                    </Box>
                }
                loading={tableLoading}
                loadingText="Loading labs"
                stickyColumns={preferences.stickyColumns}
                resizableColumns
                preferences={
                    <CollectionPreferences
                        {...Constants.COLLECTION_PREFERENCES_PROPS}
                        preferences={preferences}
                        onConfirm={({ detail }) => setPreferences(detail)}
                    />
                }
            />
        </SpaceBetween>
    );
}
